/**
 * Hebrew words for the closed vocabularies the evaluation evidence routes publish
 * (FULL-0128 / PROD-OPS-042).
 *
 * The tokens are the server's, not this file's: they are the literal members of
 * `eval_promotion_evidence.ARMS` / `PROMOTION_ELIGIBILITY` / `ARM_REASONS` and
 * `product_eval_runner.GateVerdict` / `IntegrityState` / `SuiteEscalation`. The workspace fetches
 * the server's declarations where it needs a set, and this module supplies the Hebrew for a token
 * and nothing else.
 *
 * An arm/gate/integrity token this build has no word for is named "לא מזוהה" and the raw token is
 * NOT printed as if it were an operator-facing word. That is deliberate: a vocabulary that falls
 * back to the identifier turns a producer rename into plausible-looking UI. The closed maps are
 * pinned by test so a token can disappear deliberately rather than silently.
 */

/** The one word for a token this build does not recognise. Never the raw identifier. */
export const EVALUATION_UNKNOWN_HE = "לא מזוהה";

/** A field the payload did not carry. Distinct from a reported zero. */
export const EVALUATION_NOT_REPORTED_HE = "לא דווח";

export const EVALUATION_ARM_LABELS = {
  ADOPT: "אימוץ מומלץ",
  WATCH: "מעקב",
  REJECT: "דחייה",
};

export const EVALUATION_GATE_LABELS = {
  PASS: "עבר",
  REVIEW_REQUIRED: "נדרשת סקירה",
  FAIL: "נכשל",
};

export const EVALUATION_INTEGRITY_LABELS = {
  ok: "תקין",
  suite_version_digest_mismatch: "אי־התאמה בין גרסה לדיגסט",
  case_expectation_inconsistent: "ציפיית מקרה לא עקבית",
  case_expectation_laxer_than_policy: "ציפייה מקלה מהמדיניות",
  case_label_unknown: "תווית מקרה לא מוכרת",
};

export const EVALUATION_ELIGIBILITY_LABELS = {
  eligible_for_explicit_human_promotion: "כשיר לקידום אנושי מפורש",
  held_human_review_required: "מוחזק לסקירה אנושית",
  not_promotable: "אינו ניתן לקידום",
};

/**
 * The reasons an arm carries. The first nine are
 * `eval_promotion_evidence._ARM_REASON_LOCAL`; the rest are the runner's
 * `SuiteEscalation` members, reused verbatim by the arm derivation.
 */
export const EVALUATION_ARM_REASON_LABELS = {
  suite_gate_pass: "שער המערך עבר",
  suite_gate_review_required: "שער המערך דורש סקירה",
  suite_gate_fail: "שער המערך נכשל",
  defensive_reject_dataset_integrity: "דחייה הגנתית: שלמות הדאטהסט",
  defensive_reject_critical_violation: "דחייה הגנתית: הפרה קריטית",
  defensive_reject_safety_below_one: "דחייה הגנתית: שיעור בטיחות מתחת ל־1",
  material_regression: "נסיגה מהותית",
  explicit_human_approval_required: "נדרש אישור אנושי מפורש",
  human_review_required: "נדרשת סקירה אנושית",
  dataset_integrity_failed: "שלמות הדאטהסט נכשלה",
  no_cases: "אין מקרים במערך",
  required_output_missing: "פלט נדרש חסר",
  model_tier_ineligible: "רמת המודל אינה כשירה",
  quality_below_threshold: "איכות מתחת לסף",
  critical_safety_false_negative: "החמצה קריטית של בטיחות",
  safety_below_threshold: "בטיחות מתחת לסף",
  standard_safety_violation: "הפרת בטיחות רגילה",
  unexplained_regression: "נסיגה לא מוסברת",
  review_items_present: "קיימים פריטים לסקירה",
};

function labelFrom(map, token) {
  if (typeof token !== "string" || token.length === 0) return EVALUATION_UNKNOWN_HE;
  return map[token] ?? EVALUATION_UNKNOWN_HE;
}

export const armLabel = (token) => labelFrom(EVALUATION_ARM_LABELS, token);
export const gateLabel = (token) => labelFrom(EVALUATION_GATE_LABELS, token);
export const integrityLabel = (token) => labelFrom(EVALUATION_INTEGRITY_LABELS, token);
export const eligibilityLabel = (token) => labelFrom(EVALUATION_ELIGIBILITY_LABELS, token);
export const armReasonLabel = (token) => labelFrom(EVALUATION_ARM_REASON_LABELS, token);

/**
 * A server boolean flag, read without assuming a missing key is `false`.
 *
 * `true` → "true", `false` → "false", anything else (absent, null, a string) → "unknown". This is
 * what lets the authority section say "the source did not state this" rather than inventing the
 * safe value a reader would assume.
 */
export function flagState(value) {
  if (value === true) return "true";
  if (value === false) return "false";
  return "unknown";
}

/** "כן" / "לא" / "לא דווח" for a tri-state flag. */
export function flagLabel(value) {
  const state = flagState(value);
  if (state === "true") return "כן";
  if (state === "false") return "לא";
  return EVALUATION_NOT_REPORTED_HE;
}

/** A reason token list rendered as words. An unknown token is named unknown, never echoed. */
export function armReasonLabels(tokens) {
  if (!Array.isArray(tokens)) return null;
  return tokens.map((token) => armReasonLabel(token));
}
